import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, Popconfirm, Table, Tag, Upload, message } from "antd";
import { DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import useOrganization from "../../hooks/useOrganization";
import { ACCEPTED_IMAGE_TYPES, deleteMasterImage, rejectInvalidMasterImage } from "../../utils";
import { createMasterImageFromFile } from "../../masterImages/createMasterImage";
import { unappliedChanges } from "../../masterImages/changes";
import type { BrickCollection, MasterImage } from "../../masterImages/types";

interface Props {
  brickCollection: BrickCollection | null;
  onChange(): void;
}

const MasterImages: React.FC<Props> = ({ brickCollection, onChange }) => {
  const organization = useOrganization();
  const navigate = useNavigate();
  const [progress, setProgress] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const bricks = brickCollection?.bricks ?? [];
  const editorPath = (id: string) =>
    `/${organization}/collections/${brickCollection!._id}/masterImages/${id}`;

  const upload = async ({ file, onSuccess, onError }: any) => {
    setProgress(0);
    try {
      const masterImage = await createMasterImageFromFile(
        organization,
        brickCollection!._id,
        file,
        setProgress
      );
      onSuccess(null, file);
      navigate(editorPath(masterImage._id));
    } catch (e) {
      onError(e);
      message.error((e as Error).message);
    } finally {
      setProgress(null);
    }
  };

  const remove = async (id: string) => {
    setDeletingId(id);
    try {
      await deleteMasterImage(organization, brickCollection!._id, id);
      onChange();
    } catch (e) {
      message.error((e as Error).message);
    } finally {
      setDeletingId(null);
    }
  };

  const columns = [
    {
      title: "Master image",
      key: "preview",
      render: (_: unknown, m: MasterImage) => (
        <img
          crossOrigin="anonymous"
          style={{ width: 120, cursor: "pointer" }}
          src={m.preview} onClick={() => navigate(editorPath(m._id))} />
      ),
    },
    { title: "Brick regions", key: "regions", render: (_: unknown, m: MasterImage) => m.regions.length },
    {
      title: "Unapplied changes",
      key: "unapplied",
      render: (_: unknown, m: MasterImage) => {
        const count = unappliedChanges(m, bricks);
        return count === 0 ? <Tag color="green">Up to date</Tag> : <Tag color="orange">{count}</Tag>;
      },
    },
    {
      title: "",
      key: "actions",
      width: 160,
      render: (_: unknown, m: MasterImage) => {
        const produced = bricks.filter((b) => b.masterImage === m._id).length;
        return (
          <span style={{ display: "flex", gap: 8 }}>
            <Button onClick={() => navigate(editorPath(m._id))}>Open</Button>
            <Popconfirm
              title="Delete this master image?"
              description={`This also removes its ${produced} brick image${produced === 1 ? "" : "s"} from the series.`}
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() => remove(m._id)}
            >
              <Button danger icon={<DeleteOutlined />} loading={deletingId === m._id} aria-label="Delete master image" />
            </Popconfirm>
          </span>
        );
      },
    },
  ];

  return (
    <Card
      title="Master images"
      extra={
        <Upload
          accept={ACCEPTED_IMAGE_TYPES}
          showUploadList={false}
          beforeUpload={rejectInvalidMasterImage}
          customRequest={upload}
          disabled={!brickCollection || progress != null}
        >
          <Button icon={<UploadOutlined />} loading={progress != null}>
            {progress != null ? `Uploading ${Math.round(progress)}%` : "Upload master image"}
          </Button>
        </Upload>
      }
    >
      <Table
        rowKey="_id"
        loading={brickCollection == null}
        columns={columns}
        dataSource={brickCollection?.masterImages ?? []}
        pagination={false}
      />
    </Card>
  );
};

export default MasterImages;
